import httpClient from '@/utils/httpClient';
import type { AxiosResponse } from 'axios';

// One entry of design.tabs.items. The backend serializes either PascalCase (direct DbML) or
// camelCase depending on the endpoint, so both spellings of the identity fields are allowed.
// `| undefined` everywhere: exactOptionalPropertyTypes is on, and these objects are built by
// spreading loosely-typed store items.
export interface TabItemModel {
  id?: number | undefined;
  Id?: number | undefined;
  type?: string | undefined; // 'content' | 'posts' | 'blog' | 'store' | 'photo' | 'video' | 'music' | 'files' | 'custom'
  Type?: string | undefined;
  blockType?: string | undefined;
  BlockType?: string | undefined;
  customKey?: string | undefined;
  CustomKey?: string | undefined;
  title?: string | undefined;
  Title?: string | undefined;
  icon?: string | undefined;
  isActive?: boolean | undefined;
  IsActive?: boolean | undefined;
  isAdded?: boolean | undefined;
  IsAdded?: boolean | undefined;
  n?: number | undefined;
  N?: number | undefined;
}

export interface CreateTabBody {
  type: string;
  blockType?: string | undefined;
  title?: string | undefined;
  icon?: string | undefined;
  isActive?: boolean | undefined;
  isAdded?: boolean | undefined;
  n?: number | undefined;
}

const base = (profileId: number) => `api/profiles/${profileId}/tabs`;

// Design endpoints historically receive a `timestamp` field (see useFetchDesign) — kept for parity.
const timestamp = () => new Date().toISOString();

/** Create a tab: custom tabs (type='custom' + blockType) and predefined adds share this endpoint. */
export const createTabApi = (profileId: number, body: CreateTabBody): Promise<AxiosResponse<TabItemModel>> => httpClient.post(base(profileId), { timestamp: timestamp(), ...body });

/** Delete a tab. For custom tabs the backend null-ifies Block.TabId so content survives. */
export const deleteTabApi = (profileId: number, tabId: number): Promise<AxiosResponse<void>> => httpClient.delete(`${base(profileId)}/${tabId}`);

export const renameTabApi = (profileId: number, tabId: number, title: string): Promise<AxiosResponse<void>> => httpClient.put(`${base(profileId)}/${tabId}/rename`, { title });

/** Per-tab visibility toggle — matches by tabId, safe for custom tabs (which all share type='custom'). */
export const setTabActiveApi = (profileId: number, tabId: number, isActive: boolean): Promise<AxiosResponse<void>> => httpClient.put(`${base(profileId)}/${tabId}`, { isActive });

/** Persist tab order. Custom tabs are matched by id, predefined by type. */
export const sortTabsApi = (profileId: number, items: Array<{ id?: number | undefined; type?: string | undefined; n?: number | undefined }>): Promise<AxiosResponse<void>> =>
  httpClient.put(`api/profiles/${profileId}/design/tabs/sort`, { timestamp: timestamp(), items });

/**
 * Bulk visibility for PREDEFINED tabs only — custom tabs (shared type='custom') must never
 * be sent here.
 *
 * Core's UpdateTabNavigationStatus resolves each stored tab against the payload by Id first
 * and only falls back to a by-Type SingleOrDefault (which now excludes Custom). Older Core
 * builds had no such guard and threw on 2+ custom tabs, so the filter stays until every
 * environment is known to run the current controller. Tabs missing from the payload are left
 * untouched — the endpoint only logs them — so filtering can't deactivate anything.
 */
export const setPredefinedTabsStatusApi = (profileId: number, items: TabItemModel[]): Promise<AxiosResponse<void>> =>
  httpClient.put(`api/profiles/${profileId}/design/tabs/status`, { timestamp: timestamp(), items });
